import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { menuHeaders, publicMenuApi, publicOrderKey } from '../api/publicMenu';
import { Order } from '../types';

const PaymentPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [tipWaiter, setTipWaiter] = useState(0);
  const [tipKitchen, setTipKitchen] = useState(0);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const { currentOrder } = useAppSelector((s) => s.order);

  useEffect(() => {
    if (currentOrder && currentOrder.id === Number(orderId)) setOrder(currentOrder);
  }, [currentOrder, orderId]);

  const handlePay = async () => {
    if (!order || saving) return;
    setSaving(true);
    setError('');
    try {
      const raw = sessionStorage.getItem(publicOrderKey(order.id));
      const capability: unknown = raw ? JSON.parse(raw) : null;
      if (!capability || typeof capability !== 'object' ||
          !('access' in capability) || typeof capability.access !== 'string' ||
          !('guestAccess' in capability) || typeof capability.guestAccess !== 'string') {
        setError('Сеанс замовлення недоступний. Зверніться до персоналу ресторану.');
        return;
      }
      const { data } = await publicMenuApi.put<Order>(`/orders/${order.id}/tips`,
        { tipForWaiter: tipWaiter, tipForKitchen: tipKitchen },
        { headers: menuHeaders(capability.access, capability.guestAccess) });
      setOrder(data);
      setPaid(true);
    } catch {
      setError('Не вдалося зберегти чайові. Сеанс міг завершитися; зверніться до персоналу.');
    } finally {
      setSaving(false);
    }
  };

  if (paid) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ fontSize: '80px', marginBottom: '20px' }}>✅</div>
        <h2 style={{ color: 'var(--crm-success)' }}>Побажання збережено</h2>
        <p style={{ color: 'var(--crm-text-muted)' }}>Оплату підтверджує персонал ресторану. Ця сторінка не проводить платіж.</p>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'var(--crm-navbar-dropdown-bg)',
            color: 'var(--crm-primary-contrast)',
            padding: '12px 24px',
            borderRadius: 'var(--crm-radius-sm)',
            cursor: 'pointer',
            fontSize: '16px',
            marginTop: '20px',
          }}
        >
          На головну
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <p>Сеанс замовлення недоступний. Зверніться до персоналу ресторану.</p>
        <p style={{ color: 'var(--crm-text-muted)', fontSize: '14px' }}>Замовлення #{orderId}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-success)', paddingBottom: '8px' }}>
        Замовлення #{order.id}
      </h2>
      <div
        style={{
          background: 'var(--crm-surface)',
          color: 'var(--crm-text)',
          border: '1px solid var(--crm-border)',
          borderRadius: 'var(--crm-radius-md)',
          boxShadow: 'var(--crm-shadow-sm)',
          padding: '20px',
          marginTop: '16px',
        }}
      >
        <h3 style={{ marginTop: 0, color: 'var(--crm-text)' }}>Склад замовлення</h3>
        {order.items.map((i) => (
          <div
            key={i.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid var(--crm-table-row-border)',
            }}
          >
            <span>
              {i.menuItemName} x{i.quantity}
            </span>
            <span style={{ fontWeight: 'bold' }}>{i.totalPrice.toFixed(2)} ₴</span>
          </div>
        ))}
        <div style={{ marginTop: '16px', padding: '12px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span>Сума:</span>
            <span>{order.totalPrice.toFixed(2)} ₴</span>
          </div>
          {order.appliedDiscountValue > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: 'var(--crm-danger)' }}>
              <span>Знижка:</span>
              <span>-{order.appliedDiscountValue.toFixed(2)} ₴</span>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 'bold',
              fontSize: '18px',
              color: 'var(--crm-success)',
            }}
          >
            <span>До оплати:</span>
            <span>{order.finalPrice.toFixed(2)} ₴</span>
          </div>
        </div>

        <h3 style={{ color: 'var(--crm-text)', marginTop: '20px' }}>Чайові (необов'язково)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: 'var(--crm-text)' }}>Офіціанту (₴)</label>
            <input
              type="number"
              min="0"
              value={tipWaiter}
              onChange={(e) => setTipWaiter(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--crm-border)',
                borderRadius: 'var(--crm-radius-sm)',
                background: 'var(--crm-input-bg)',
                color: 'var(--crm-text)',
                fontSize: '16px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', color: 'var(--crm-text)' }}>Кухні (₴)</label>
            <input
              type="number"
              min="0"
              value={tipKitchen}
              onChange={(e) => setTipKitchen(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--crm-border)',
                borderRadius: 'var(--crm-radius-sm)',
                background: 'var(--crm-input-bg)',
                color: 'var(--crm-text)',
                fontSize: '16px',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <button
          onClick={handlePay}
          disabled={saving || tipWaiter < 0 || tipKitchen < 0}
          style={{
            width: '100%',
            padding: '14px',
            background: 'var(--crm-success-bg)',
            color: 'var(--crm-success)',
            border: '1px solid var(--crm-success)',
            borderRadius: 'var(--crm-radius-sm)',
            cursor: 'pointer',
            fontSize: '18px',
            marginTop: '20px',
            fontWeight: 'bold',
          }}
        >
          {saving ? 'Збереження...' : 'Зберегти чайові'} · {(order.finalPrice + tipWaiter + tipKitchen).toFixed(2)} ₴
        </button>
        <p>Оплату підтверджує персонал ресторану. Онлайн-платіж не проводиться.</p>
        {error && <p role="alert">{error}</p>}
      </div>
    </div>
  );
};

export default PaymentPage;
